import React, { useState } from 'react';

import { isLateSubmissionReachable, requiresLateSubmissionReason } from '~support-at-home/lib/lateSubmission';

import { ClickableIcon, Modal, TabContainer } from '~components';
import Icon, { faPlus, faTriangleExclamation } from '~components/icon';
import { Tab } from '~components/TabContainer';

import { ClaimItem } from '~support-at-home/models';

import ClaimItemAttachmentsPanel from './ClaimItemAttachmentsPanel';
import ClaimItemLateSubmissionPanel from './ClaimItemLateSubmissionPanel';

interface ComponentProps {
  itemId: string;
  /**
   * Claim the item belongs to. Optional: without it the panels' mutations can only invalidate
   * their own item-level tags, leaving the `sa_status` cell and the claim banner stale until some
   * other refetch runs. Every real call site has it - the optionality exists so the component
   * stays renderable from a row that has not loaded its claim association.
   */
  claimId?: string;
  row: ClaimItem;
}

/**
 * ClaimItemRequirements
 *
 * Single trigger + tabbed modal for everything Services Australia needs on a claim item beyond
 * the plain push (TAV-2592) - attachments (TAV-2591) and the late submission reason, previously
 * two separate trigger+modal pairs (ClaimItemAttachments, ClaimItemLateSubmission) each with their
 * own `sa_status`-adjacent icon or column. Combined into one `+` trigger with a tab per applicable
 * requirement, modelled on the details-icon pattern in ServiceDetail.tsx.
 *
 * Renders nothing at all when neither requirement applies to this item - there is nothing for the
 * trigger to open.
 */
interface TabVisibility {
  attachments: boolean;
  lateSubmission: boolean;
}

const ClaimItemRequirements: React.FC<ComponentProps> = ({ itemId, claimId, row }) => {
  const [isOpen, setIsOpen] = useState(false);
  // The tab set as the LIVE row says it should be, recomputed every render.
  const liveTabs: TabVisibility = {
    attachments: Boolean(row.sa_attachment_required || row.sa_attachment_provided),
    lateSubmission: isLateSubmissionReachable(row),
  };
  // ...and the tab set as it was when the modal opened. Both exist because the panels' own mutations
  // invalidate this row, so `liveTabs` can change WHILE the modal is open: delete the item's only
  // attachment and `attachments` flips false mid-session. That took the Attachments tab out from
  // under the user - and if they were on the Late Submission tab (index 1) at the time, TabContainer
  // was left holding an index into a one-tab set and rendered a blank body. TabContainer now clamps
  // that index, so this is the second of two independent guards: the clamp makes the crash
  // impossible, and freezing the composition for the modal's open lifetime means the user is not
  // silently moved to a different tab in the first place. The next open re-latches from live data.
  const [latchedTabs, setLatchedTabs] = useState<TabVisibility | null>(null);
  const visibleTabs = isOpen && latchedTabs ? latchedTabs : liveTabs;

  if (!visibleTabs.attachments && !visibleTabs.lateSubmission) return null;

  // Something is required but not yet satisfied - drives the small red exclamation badge on the
  // trigger. Shared predicate with ClaimSahPushIcon's needs-attention popover, so the two cannot
  // disagree about whether this item still needs action.
  const needsAction =
    (Boolean(row.sa_attachment_required) && !row.sa_attachment_provided) ||
    (requiresLateSubmissionReason(row) && !row.late_submission_reason_code);

  const tabs: Tab[] = [];
  if (visibleTabs.attachments) {
    tabs.push({
      id: `claim-item-requirements-attachments-${itemId}`,
      title: 'Attachments',
      content: <ClaimItemAttachmentsPanel itemId={itemId} claimId={claimId} isOpen={isOpen} />,
    });
  }
  if (visibleTabs.lateSubmission) {
    tabs.push({
      id: `claim-item-requirements-late-submission-${itemId}`,
      title: 'Late Submission Reason',
      content: <ClaimItemLateSubmissionPanel itemId={itemId} claimId={claimId} row={row} isOpen={isOpen} />,
    });
  }

  return (
    <>
      <Modal isOpen={isOpen} setIsOpen={setIsOpen} header="Claim item requirements" size="lg" closeButtonText="Close">
        {/* setPageTitle={false}: TabContainer defaults to calling setTitle(...) on tab change,
            which would clobber the page title from inside a modal. */}
        <TabContainer tabs={tabs} setPageTitle={false} />
      </Modal>
      <div className="d-flex align-items-center justify-content-center position-relative">
        <ClickableIcon
          id={`claim-item-requirements-${itemId}`}
          icon={faPlus}
          // Latch the tab composition here rather than in an effect: this is the moment the modal
          // opens, so it is the moment the set is decided.
          onClick={() => {
            setLatchedTabs(liveTabs);
            setIsOpen(true);
          }}
          ariaLabel="Manage requirements for this item"
          toolTip="Manage requirements"
          dataCy={`claim-item-requirements-button-${itemId}`}
        />
        {/* `title` is what puts this badge in the accessibility tree at all - it is the SOLE
            indicator that the item has an outstanding requirement, so without it the whole signal is
            invisible to assistive technology. Same pattern as the unconfirmed-attachment warning in
            ClaimItemAttachmentsPanel. */}
        {needsAction && (
          <Icon icon={faTriangleExclamation} className="icon-alert" title="Action required for this item" />
        )}
      </div>
    </>
  );
};

export default ClaimItemRequirements;
